import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import {
  paidChannelsTableHeaderData,
  paidChannelSumAssuredData,
  paidChannelTableRowData,
} from "@/pages/overView/utils/dummydata";
import React, { JSX, useEffect, useState } from "react";
import GridCard from "./components/GridCard";
import GaugeCard from "./components/gauge-card";
import moment from "moment";
import GraphView from "./components/graph";
import { MetricsDisplayTable } from "./components/metrics-display-table";
import CustomSelect from "./components/select";
import {
  ctvIcon,
  downArrowOrangeBgIcon,
  fbIcon,
  instaIcon,
  othersIcon,
  snapChatIcon,
  tiktokIcon,
  upArrowGreenBgIcon,
  youtubeIcon,
} from "./resources/images";
import CustomDatePicker from "./components/date-picker";
import "./theme.module.css";
import { OverViewContainer } from "./styles";

type FieldData = {
  type: "date" | "select" | "button";
  dataKey?: string;
  label?: string;
  options?: { value: string; label: string }[];
};

type ComponentProps = {
  text?: string;
  data?: string;
  width?: number;
  height?: number;
  float?: "left" | "right" | "none" | "inline-start" | "inline-end";
  value?: string;
  trendValue?: string;
  trendType?: string;
  key?: string;
};

type Component = {
  type: string;
  props: ComponentProps;
};

type Row = {
  width: number;
  height?: number;
  components?: Component[];
  content?: Row[];
};

type Section = {
  title?: string;
  content: Row[];
  components: Component[];
};

type YamlData = {
  fields: Record<string, FieldData>;
  report: Section[];
};

type JsonData = {
  data: Record<string, any>;
  meta: {
    translations: Record<string, any>;
  };
};

type OverViewProps = {
  headers: Record<string, string>;
  reportId: string;
  accessToken: string;
  apiUrl?: string;
};

const OverView: React.FC<OverViewProps> = ({
  headers,
  reportId,
  accessToken,
  apiUrl,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [yamlData, setYamlData] = useState<YamlData | null>(null);
  const [jsonData, setJsonData] = useState<JsonData | null>(null);

  useEffect(() => {
    getYamlData();
  }, [reportId, accessToken, headers]);

  const getYamlData = async () => {
    try {
      const response = await fetch(`${apiUrl}/${reportId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...headers,
        },
      });
      const data = await response.json();
      setYamlData(data.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const getMockData = async (data: {
    start_date: Date | null;
    end_date: Date | null;
    selected_item: string;
  }) => {
    setJsonData(null);
    try {
      setLoading(true);
      const response = await fetch(
        `http://192.168.29.28:8000/api/report/get-report-data/${reportId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...headers,
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setTimeout(() => {
        setLoading(false);
        setJsonData(result.data);
      }, 3000);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching mock data:", error);
    }
  };

  const handleSelectChange = (value: any) => {
    setSelectedValue(value);
  };

  const handleSubmit = () => {
    if (startDate && endDate && selectedValue) {
      getMockData({
        start_date: startDate,
        end_date: endDate,
        selected_item: selectedValue.value,
      });
    } else {
      alert("Please select start date, end date and select value");
    }
  };

  const resolveDynamicData = (key: any) => jsonData?.data[key] || {};

  const resolveDynamicText = (key: any) => {
    const text = jsonData?.meta.translations[key];
    if (text) {
      return typeof text === "object" ? JSON.stringify(text) : text;
    } else {
      return text;
    }
  };

  const getUriResponse = async (uri: string) => {
    const lastId = uri.split("/").pop();
    try {
      const response = await fetch(
        `http://192.168.29.28:8000/api/report/get-uri-data/${lastId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...headers,
          },
        }
      );
      const data = await response.json();
      return data.data;
    } catch (error) {
      setLoading(false);
      console.error("Error fetching mock data:", error);
    }
  };

  const getChatUriResponse = async (uri: string) => {
    const lastId = uri.split("/").pop();
    try {
      const response = await fetch(
        `http://192.168.29.28:8000/api/report/get-chart-uri-data/${lastId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...headers,
          },
        }
      );
      const data = await response.json();
      return data.data;
    } catch (error) {
      setLoading(false);
      console.error("Error fetching mock data:", error);
    }
  };

  const renderField = (fieldKey: string, fieldData: FieldData) => {
    const dynamicData = resolveDynamicData(fieldData?.dataKey);
    switch (fieldData.type) {
      case "date":
        return (
          <CustomDatePicker
            key={fieldKey}
            value={fieldKey === "period_start" ? startDate : endDate}
            onChange={(e: any) =>
              fieldKey === "period_start" ? setStartDate(e) : setEndDate(e)
            }
          />
        );

      case "select":
        return (
          <CustomSelect
            key={fieldKey}
            value={selectedValue || null}
            setSelectedValue={handleSelectChange}
            options={dynamicData.options || fieldData.options || []}
            placeholder="Select a locale"
            selected={selectedValue?.value || ""}
          />
        );
      case "button":
        return (
          <Button
            key={fieldKey}
            variant="contained"
            color="warning"
            onClick={handleSubmit}
          >
            {fieldData.label || "Submit"}
          </Button>
        );
      default:
        return null;
    }
  };

  const renderComponent = (component: Component, index: number) => {
    const { type, props } = component;
    switch (type) {
      case "trend_indicator":
        return (
          <Box
            key={`component-${index}`}
            style={{
              width: `${props.width}%`,
              padding: "10px 10px 5px 10px",
            }}
          >
            <Avatar
              src={upArrowGreenBgIcon}
              alt="upArrowGreenBgIcon"
              sx={{
                width: "20px",
                height: "20px",
              }}
            />
          </Box>
        );
      case "semi-circle":
        return props.data ? (
          <SemiCircleGauge dataKey={props.data.key} index={index} />
        ) : null;
      case "text":
        return (
          <Typography
            key={`text-${index}`}
            variant="subtitle1"
            style={{
              width: `${props.width}%`,
              padding: "10px 10px 5px 10px",
            }}
          >
            {resolveDynamicText(props.text.key)}
          </Typography>
        );
      case "value":
        const valueData = resolveDynamicData(props.value.key);
        return (
          <div
            key={`component-${index}`}
            style={{
              width: `${props.width}%`,
              float: props.float,
              padding: "10px 10px 5px 10px",
            }}
          >
            <span>{valueData.formatted_value}</span>
          </div>
        );
      case "line_chart":
        return props.data ? (
          <LineChatComponent dataKey={props.data.key} index={index} />
        ) : null;
      case "trend_with_icon":
        const trend_with_icon = resolveDynamicData(props.value.key);
        const valueDifference =
          trend_with_icon.value - trend_with_icon.previous_value;
        const percentageDifference = (
          (valueDifference / trend_with_icon.previous_value) *
          100
        ).toFixed(2);

        const formattedDifference =
          valueDifference > 0
            ? `+${percentageDifference}%`
            : valueDifference < 0
            ? `-${Math.abs(percentageDifference)}%`
            : "0%";

        const icon =
          valueDifference > 0 ? upArrowGreenBgIcon : downArrowOrangeBgIcon;

        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              gap: "2px",
              padding: "0px 10px 5px 10px",
            }}
          >
            <Box
              key={`component-${index}`}
              style={{
                width: `${props.width}%`,
              }}
            >
              <Avatar
                src={icon}
                alt="upArrowGreenBgIcon"
                sx={{
                  width: "20px",
                  height: "20px",
                }}
              />
            </Box>
            <div
              key={`component-${index}`}
              style={{ padding: "10px", float: props.float }}
            >
              <p
                style={{
                  color:
                    valueDifference > 0
                      ? "var(--forest-green)"
                      : "var(--vibrant-orange)",
                }}
              >{`${formattedDifference}`}</p>
            </div>
          </Box>
        );
      case "icons":
        const icons = props.data;

        return (
          <Box
            sx={{
              display: "inline-flex",
              gap: "10px",
              padding: "10px",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            {icons.map((icon: string, index: number) => {
              const iconPath = `/images/${icon}`;
              return (
                // <p>{icon}</p>
                <img
                  key={`icon-${index}`}
                  src={
                    icon === "snapchat.svg"
                      ? snapChatIcon
                      : icon === "tiktok.svg"
                      ? tiktokIcon
                      : icon === "instagram.svg"
                      ? instaIcon
                      : icon === "youtube.svg"
                      ? youtubeIcon
                      : icon === "facebook.svg"
                      ? fbIcon
                      : icon === "ctv.svg"
                      ? ctvIcon
                      : icon === "other.svg"
                      ? othersIcon
                      : ""
                  }
                  alt={icon}
                  style={{
                    width: "24px",
                    height: "24px",
                    objectFit: "contain",
                  }}
                />
              );
            })}
          </Box>
        );
      case "list":
        return (
          <MetricsDisplayTable
            tableHeadersData={paidChannelsTableHeaderData}
            tableRowsData={paidChannelTableRowData}
            tableSumAssuredData={paidChannelSumAssuredData}
          />
        );
      default:
        return null;
    }
  };

  const renderReportContent = (content: Row[]): JSX.Element[] => {
    const maxWidth = 12;
    let currentWidth = 0;

    return content
      .filter((row) => {
        // Filter out rows with no data in components or content
        const componentsWithData = row.components?.filter((component) => {
          console.log("component", component);
          const staticData = component.props.data
            ? resolveDynamicData(component.props.data.key)
            : component.props.value
            ? resolveDynamicData(component.props.value.key)
            : component.props.width
            ? component.props.width
            : {};
          return Object.values(staticData).length > 0;
        });

        const filteredContent = row.content
          ? renderReportContent(row.content)
          : null;

        return (
          (componentsWithData && componentsWithData.length > 0) ||
          (filteredContent && filteredContent.length > 0)
        );
      })
      .map((row, rowIndex) => {
        if (currentWidth + row.width > maxWidth) {
          currentWidth = row.width;
        } else {
          currentWidth += row.width;
        }

        const gridColumnSpan = Math.min(row.width, maxWidth);

        return (
          <div
            key={`row-${rowIndex}`}
            style={{
              gridColumn: `span ${gridColumnSpan}`,
              gridRow: row.height ? `span ${row.height}` : undefined,
              alignItems: "stretch",
            }}
          >
            <GridCard
              cusStyle={{
                display: "grid",
                gridTemplateColumns: "repeat(12, 1fr)",
                backgroundColor: "#fff",
                height: "100%",
              }}
            >
              {row.components && renderComponents(row.components)}
            </GridCard>

            {row.content && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, 1fr)",
                  gap: "10px",
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  height: "100%",
                }}
              >
                {renderReportContent(row.content)}
              </div>
            )}
          </div>
        );
      });
  };

  const renderComponents = (components: Component[]) => {
    return components
      .filter((component) => {
        const staticData = component.props.data
          ? resolveDynamicData(component.props.data.key)
          : component.props.value
          ? resolveDynamicData(component.props.value.key)
          : component.props.text
          ? resolveDynamicText(component.props.text.key)
          : {};
        return Object.values(staticData).length > 0; // Only include components with data
      })
      .map((component, index) => {
        const componentWidth = component.props?.width || 12;
        return (
          <div
            key={`component-${index}`}
            style={{
              gridColumn: `span ${componentWidth}`,
              display: "grid",
              placeContent: component.props.float ? "end" : "left",
            }}
          >
            {renderComponent(component, index)}
          </div>
        );
      });
  };

  const SemiCircleGauge = ({
    dataKey,
    index,
  }: {
    dataKey: string;
    index: number;
  }) => {
    const [semiCircleData, setSemiCircleData] = React.useState<any>(null);

    React.useEffect(() => {
      const data = resolveDynamicData(dataKey);
      if (data.uri) {
        getUriResponse(data.uri).then((uriData) => {
          console.log("Fetched URI Data:", uriData);
          setSemiCircleData(uriData);
        });
      } else {
        setSemiCircleData(data);
      }
    }, [dataKey]);

    if (!semiCircleData) {
      // Display a loading state
      return <div>Loading...</div>;
    }

    return (
      <GaugeCard
        key={`semi-${index}`}
        label="Visitors"
        visitors={semiCircleData.value}
        goal={semiCircleData.previous_value}
        percentage={(
          (semiCircleData.value / semiCircleData.previous_value) *
          100
        ).toFixed(2)}
      />
    );
  };

  const LineChatComponent = ({
    dataKey,
    index,
  }: {
    dataKey: string;
    index: number;
  }) => {
    const [chartData, setChartData] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
      const fetchData = async () => {
        const chat_Data = resolveDynamicData(dataKey);

        if (chat_Data.uri) {
          try {
            const data = await getChatUriResponse(chat_Data.uri);
            setChartData(data);
          } catch (error) {
            console.error("Error fetching data:", error);
          }
        } else {
          setChartData(chat_Data);
        }

        setLoading(false);
      };

      fetchData();
    }, [dataKey]);

    if (loading) {
      return <Typography sx={{ padding: "10px" }}>Loading data...</Typography>;
    }

    if (
      !chartData ||
      !chartData.chart ||
      !chartData.chart.data ||
      chartData.chart.data.length === 0
    ) {
      return (
        <Typography sx={{ padding: "10px" }}>
          No data available for the chart
        </Typography>
      );
    }
    const labels = chartData.chart.data.map((item: any) =>
      moment(item.label).format("DD MMM")
    );
    const previous = chartData.chart.data.map((item: any) => item.previous);
    const value = chartData.chart.data.map((item: any) => item.value);

    return (
      <GraphView
        key={`component-${index}`}
        labels={labels}
        dataPoints2023={previous}
        dataPoints2024={value}
      />
    );
  };

  return (
    <Box sx={OverViewContainer.container}>
      {yamlData?.fields && (
        <Box sx={OverViewContainer.headerWrapper}>
          <Typography variant="h5">Dynamic UI</Typography>
          <Box
            sx={{
              display: "grid",
              gap: "16px",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            }}
          >
            {yamlData?.fields &&
              Object.entries(yamlData?.fields)?.map(([key, field]) =>
                renderField(key, field)
              )}
          </Box>
        </Box>
      )}

      {jsonData && !loading ? (
        <Box
          sx={{
            marginTop: "20px",
            marginBottom: "20px",
            paddingLeft: "36px",
            paddingRight: "36px",
            display: "flex",
            gap: "20px",
            flexDirection: "column",
          }}
        >
          {yamlData?.report &&
            yamlData?.report?.map((section, sectionIndex) => {
              return (
                <div key={`section-${sectionIndex}`}>
                  {section.title && (
                    <Typography variant="h6">
                      {typeof section.title === "object"
                        ? section.title.key
                        : resolveDynamicText(section.title.key)}
                    </Typography>
                  )}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(12, 1fr)",
                      gap: "16px",
                    }}
                  >
                    {renderReportContent(section.content || [section])}
                  </div>
                </div>
              );
            })}
        </Box>
      ) : (
        loading && (
          <Box
            sx={{
              width: "100%",
              height: "200px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        )
      )}
    </Box>
  );
};

export default OverView;
